import { type EventType } from "@modules/calendar/calendarTypes"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { verboseDateIntervalFormat, weekdayDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { capitalize } from "lodash"
import { createContext, type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react"

export type CalendarContextProps = {
  isUndated: boolean
  isWeekTemplate: boolean
  hasHours: boolean
  maxDayIndex?: number
  currentDayIndex?: number
  selectedDate: dayjs.Dayjs | number | undefined
  setSelectedDate: Dispatch<SetStateAction<dayjs.Dayjs | number | undefined>>
  displayDetailedEvents: boolean
  setDisplayDetailedEvents: Dispatch<SetStateAction<boolean>>
  currentWeekTitle: string
  currentWeekdays:
    | { dayIndex?: number; weekdayIndex: number; date?: dayjs.Dayjs; label: string; isToday: boolean }[]
    | undefined
  currentView: AvailableViews
  setCurrentView: Dispatch<SetStateAction<AvailableViews>>
  refreshCalendarEvents: () => void
  draggingEvent: EventType | undefined
  setDraggingEvent: (arg0: EventType | undefined) => void
  isDragging: boolean
  setIsDragging: (arg0: boolean) => void
  getCalendarRange: () =>
    | { startDate: number; endDate: number }
    | { startDate: dayjs.Dayjs | undefined; endDate: dayjs.Dayjs | undefined }
    | { startDate: undefined; endDate: undefined }
}

export const CalendarContext = createContext<CalendarContextProps>({} as CalendarContextProps)

export type AvailableViews = "dayGridWeek"

type CalendarProviderProps = {
  isUndated?: boolean
  isWeekTemplate?: boolean
  hasHours?: boolean
  maxDayIndex?: number
  currentDayIndex?: number | null
  initialDate: dayjs.Dayjs | undefined
  onDateSet?: ({
    startDate,
    endDate,
  }: { startDate: number; endDate: number } | { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs }) => void
  refreshEvents?: ({
    startDate,
    endDate,
  }: { startDate: number; endDate: number } | { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs }) => void
  defaultDisplayDetailedEvents: boolean
  defaultView: AvailableViews
  children: React.ReactNode
}

export function CalendarProvider({
  isUndated = true,
  isWeekTemplate = false,
  hasHours = false,
  initialDate,
  maxDayIndex,
  currentDayIndex = 0,
  onDateSet,
  refreshEvents,
  defaultDisplayDetailedEvents = false,
  defaultView = "dayGridWeek",
  children,
}: CalendarProviderProps) {
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | number | undefined>()
  const [displayDetailedEvents, setDisplayDetailedEvents] = useState<boolean>(defaultDisplayDetailedEvents)
  const [currentWeekTitle, setCurrentWeekTitle] = useState<string>("")
  const [draggingEvent, setDraggingEvent] = useState<EventType>()
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [currentWeekdays, setCurrentWeekdays] = useState<
    | {
        dayIndex?: number
        weekdayIndex: number
        date?: dayjs.Dayjs
        label: string
        isToday: boolean
      }[]
    | undefined
  >()
  const [currentView, setCurrentView] = useState<AvailableViews>(defaultView)

  const language = useLanguage()

  // Store the callback in a ref so it's always current but doesn't trigger re-renders
  const onDateSetRef = useRef(onDateSet)
  useEffect(() => {
    onDateSetRef.current = onDateSet
  }, [onDateSet])

  const refreshCalendarEvents = () => {
    if (refreshEvents) {
      if (isUndated && typeof selectedDate === "number") {
        refreshEvents({ startDate: selectedDate, endDate: selectedDate + 7 })
      } else {
        const firstDayOfWeek = dayjs(selectedDate).weekday(0)
        const lastDayOfWeek = dayjs(selectedDate).weekday(6)
        refreshEvents({ startDate: firstDayOfWeek, endDate: lastDayOfWeek })
      }
    }
  }

  function getCalendarRange() {
    if (selectedDate === undefined && currentWeekdays === undefined) {
      return { startDate: undefined, endDate: undefined }
    }
    if (isWeekTemplate && currentWeekdays && currentWeekdays.length > 0) {
      const weekStart = currentWeekdays[0]
      const weekEnd = currentWeekdays[currentWeekdays.length - 1]
      return {
        startDate: weekStart?.dayIndex ?? 0,
        endDate: weekEnd?.dayIndex ?? 6,
      }
    }
    if ((isUndated && typeof selectedDate === "number") || selectedDate === undefined) {
      return { startDate: selectedDate ?? 0, endDate: (selectedDate ?? 0) + 7 }
    }
    const firstDayOfWeek = dayjs(selectedDate).startOf("day").weekday(0)
    const lastDayOfWeek = dayjs(selectedDate).endOf("day").weekday(6)
    return { startDate: firstDayOfWeek, endDate: lastDayOfWeek }
  }

  useEffect(() => {
    setSelectedDate(initialDate || (isUndated ? 0 : dayjs()))
  }, [initialDate?.valueOf()])

  useEffect(() => {
    if (isWeekTemplate) {
      setCurrentWeekTitle(`${capitalize(word("global.week.label", { count: 1 }))}`)
      const weekdays = []
      for (let i = 0; i < 7; i++) {
        weekdays.push({
          dayIndex: i,
          weekdayIndex: i,
          isToday: currentDayIndex === i,
          label: `${dayjs(selectedDate).weekday(i).format("dddd")}`,
        })
      }
      setCurrentWeekdays(weekdays)
    } else if (isUndated && typeof selectedDate === "number") {
      onDateSetRef.current?.({ startDate: selectedDate, endDate: selectedDate + 7 })
      setCurrentWeekTitle(`${capitalize(word("global.week.label", { count: 1 }))} ${selectedDate / 7 + 1}`)
      const weekdays = []
      for (let i = 0; i < 7; i++) {
        weekdays.push({
          dayIndex: selectedDate + i,
          weekdayIndex: i,
          isToday: currentDayIndex === selectedDate + i,
          label: `${capitalize(word("global.day.label", { count: 1 }))} ${selectedDate + i + 1}`,
        })
      }
      setCurrentWeekdays(weekdays)
    } else if (selectedDate) {
      const firstDayOfWeek = dayjs(selectedDate).weekday(0)
      const lastDayOfWeek = dayjs(selectedDate).weekday(6)

      onDateSetRef.current?.({ startDate: firstDayOfWeek, endDate: lastDayOfWeek })

      setCurrentWeekTitle(verboseDateIntervalFormat(firstDayOfWeek, lastDayOfWeek, language))

      const weekdays = []
      for (let i = 0; i < 7; i++) {
        weekdays.push({
          weekdayIndex: i,
          date: dayjs(selectedDate).weekday(i),
          isToday: dayjs(selectedDate).weekday(i).isSame(dayjs(), "day"),
          label: dayjs(selectedDate).weekday(i).format(weekdayDateFormat(language)),
        })
      }

      setCurrentWeekdays(weekdays)
    }
  }, [selectedDate?.valueOf(), dayjs.locale(), language])

  const value = {
    selectedDate,
    setSelectedDate,
    currentWeekTitle,
    displayDetailedEvents,
    setDisplayDetailedEvents,
    currentWeekdays,
    currentView,
    setCurrentView,
    refreshCalendarEvents,
    getCalendarRange,
    isUndated,
    isWeekTemplate,
    hasHours,
    maxDayIndex,
    draggingEvent,
    setDraggingEvent,
    isDragging,
    setIsDragging,
  }

  return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>
}
